import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Col,
  Dropdown,
  Menu,
  Row,
  Typography,
} from "antd";
import { journalsQueries, DocumentFileRow } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { useValidateTitle } from "@/features/journal-title-validation";
import { RepresentationDocumentationPermissionModal } from "@/features/representation-documentation-permission-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { routes, useNavigate } from "@/shared/lib/react-router";
import {
  fallbackString,
  formatDate,
  useIsUnifiedMode,
} from "@/shared/utils";
import type { components } from "@entities/journal";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";

type Permission = components["schemas"]["DocumentationPermission"];

interface IDocumentationPermissionTableNew {
  isViewMode?: boolean;
}

export const DocumentationPermissionTableNew = ({ isViewMode = false }: IDocumentationPermissionTableNew) => {
  const navigate = useNavigate();
  const isUnifiedMode = useIsUnifiedMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { projectId, titleId } = useCoordinator();
  const { data, isFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { errorList } = useValidateTitle();

  const validationErrors = useMemo(() => {
    return errorList.filter((error) => error.id === 999);
  }, [errorList]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const handleAdminClick = () => {
    navigate(routes.project.root({ projectId }));
  };

  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;

  const {
    mutate: updateDocumentationPermission,
    isPending: isUpdatingDocumentationPermission,
  } = $journalApi.useMutation("put", "/api/v1/journals/title/{titleId}/docs", {
    body: (data: any) => data,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
      });
    },
  });

  const {
    mutate: deleteDocumentationPermission,
    isPending: isDeletingDocumentationPermission,
  } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/docs",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleDelete = (permission: Permission) => {
    deleteDocumentationPermission({
      params: {
        path: { titleId },
      },
      body: {
        documentationPermission: {
          documentationPermissionId: permission.documentationPermissionId,
        },
      },
    });
  };

  const handleSelect = (permission: Permission) => {
    onClose();
    updateDocumentationPermission({
      params: {
        path: { titleId },
      },
      body: {
        documentationPermission: {
          documentationPermissionId: permission.documentationPermissionId,
          selectedDocumentPermission: true,
        },
      },
    });
  };

  const permissions =
    data?.mainJournal?.titleJournal?.documentation?.documentationPermission;

  const activePermission = permissions?.find(
    (p) => p.selectedDocumentPermission,
  );

  const hasMultiplePermissions = (permissions?.length ?? 0) > 1;

  if (isUpdatingDocumentationPermission || isFetching) {
    return (
      <CollapsibleSection
        title="Разрешение на строительство"
        sectionId="documentation-permission"
        spinning
        validationErrors={validationErrors}
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={!activePermission}
      >
        <></>
      </CollapsibleSection>
    );
  }

  if (
    // @ts-expect-error permissions отсутствует в openapi типе documentation
    permissions?.length > 0 &&
    !activePermission &&
    !isDeletingDocumentationPermission
  ) {
    return (
      <CollapsibleSection
        title="Разрешение на строительство"
        sectionId="documentation-permission"
        spinning={false}
        validationErrors={validationErrors}
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={!activePermission}
      >
        <>
          {titleStatus ? (
            <Can I="edit" a="title_journals">
              <Button
                type="primary"
                style={{ marginTop: "14px" }}
                onClick={onOpen}
              >
                Выбрать
              </Button>
              <RepresentationDocumentationPermissionModal
                open={isOpen}
                onClose={onClose}
                onSelect={handleSelect}
                // @ts-expect-error проп не совпадает с openapi типом компонента
                permissions={permissions}
                title="Выберите разрешение на строительство"
              />
            </Can>
          ) : null}
        </>
      </CollapsibleSection>
    );
  }

  if (activePermission) {
    return (
      <CollapsibleSection
        title="Разрешение на строительство"
        spinning={false}
        validationErrors={validationErrors}
        sectionId="documentation-permission"
        getValidationErrorsForSection={getValidationErrorsForSection}
        isEmpty={false}
      >
        <>
          <div className="journal-title-section-header journal-title-section-header--tight-top">
            <Typography.Title level={5} style={{ fontWeight: "400" }}>
              Разрешение на строительство №{" "}
              {fallbackString(activePermission.number)}
            </Typography.Title>
            {hasMultiplePermissions && titleStatus ? (
              <Can I="edit" a="title_journals">
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="delete"
                        icon={<BasketIcon />}
                        onClick={() => handleDelete(activePermission)}
                      >
                        Удалить
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={["click"]}
                >
                  <div className="journal-title-menu-trigger">
                    <MoreOneIcon width={24} height={24} />
                  </div>
                </Dropdown>
              </Can>
            ) : null}
          </div>

          <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
            <Col span={8} style={{ color: "#888888" }}>
              Дата выдачи
            </Col>
            <Col span={16}>
              {fallbackString(formatDate(activePermission.date))}
            </Col>

            <Col span={8} style={{ color: "#888888" }}>
              Наименование органа, выдавшего разрешение
            </Col>
            <Col span={16}>{fallbackString(activePermission.issued)}</Col>

            <Col span={8} style={{ color: "#888888" }}>
              Срок действия
            </Col>
            <Col span={16}>
              {fallbackString(formatDate(activePermission.expiryDate))}
            </Col>

            <Col span={8} style={{ color: "#888888" }}>
              Продлён до
            </Col>
            <Col span={16}>
              {fallbackString(formatDate(activePermission.prolongDate))}
            </Col>

            {activePermission.permissionFile?.file && (
              <>
                <Col span={8} style={{ color: "#888888" }}>
                  Файл разрешения
                </Col>
                <Col span={16} style={{ display: "flex", flexDirection: "row" }}>
                  {activePermission.permissionFile.file.fileName ? (
                    <DocumentFileRow
                      inline
                      variant="plain"
                      downloadIconPosition="right"
                      fileName={activePermission.permissionFile.file.fileName}
                      fileId={activePermission.permissionFile.file.fileId}
                      disableLinkAction={isUnifiedMode}
                    />
                  ) : (
                    "-"
                  )}
                </Col>
              </>
            )}
          </Row>
        </>
      </CollapsibleSection>
    );
  }
  return (
    <CollapsibleSection
      title="Разрешение на строительство"
      spinning={false}
      validationErrors={validationErrors}
      sectionId="documentation-permission"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty
    >
      {isViewMode ? (
        <div className="journal-empty-text">
          Данные не были заполнены
        </div>
      ) : (
        <div className="journal-empty-text">
          В карточке объекта указано, что разрешение на строительство не
          требуется. Обратитесь к{" "}
          <span
            onClick={handleAdminClick}
            className="journal-title-link-action"
          >
            администратору проекта
          </span>{" "}
          если требуется изменить настройку.
        </div>
      )}
    </CollapsibleSection>
  );
};

